import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot Group Definitions ───────────────────────────────────────────────────

// Three image tiles, each bound to a photo_gallery item by default (indices
// 0–2). Picking a different media item in the editor swaps image + alt together.
const galleryImage0Def: SlotGroupDefinition = {
  label: 'Gallery Image 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select an appealing, high-quality photo of this property — an interior, amenity, or exterior that shows the community at its best.',
    context:
      'First of three side-by-side tiles in the home page gallery grid; the three tiles together should feel varied, so each tile must use a different photo and ideally a different subject.',
    sourceCollection: 'photo_gallery',
  },
};

const galleryImage1Def: SlotGroupDefinition = {
  label: 'Gallery Image 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select an appealing, high-quality photo of this property — an interior, amenity, or exterior that shows the community at its best.',
    context:
      'Second of three side-by-side tiles in the home page gallery grid; each tile must use a different photo from the others and ideally a different subject.',
    sourceCollection: 'photo_gallery',
  },
};

const galleryImage2Def: SlotGroupDefinition = {
  label: 'Gallery Image 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select an appealing, high-quality photo of this property — an interior, amenity, or exterior that shows the community at its best.',
    context:
      'Third of three side-by-side tiles in the home page gallery grid; each tile must use a different photo from the others and ideally a different subject.',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────

export function HomeGalleryGrid() {
  const galleryImage0 = useSlotGroup('homegallerygrid.gallery_image_0', galleryImage0Def);
  const galleryImage1 = useSlotGroup('homegallerygrid.gallery_image_1', galleryImage1Def);
  const galleryImage2 = useSlotGroup('homegallerygrid.gallery_image_2', galleryImage2Def);

  const image0Url = galleryImage0.image || '';
  const image0Alt = galleryImage0.alt || 'Gallery Image 1';
  const image1Url = galleryImage1.image || '';
  const image1Alt = galleryImage1.alt || 'Gallery Image 2';
  const image2Url = galleryImage2.image || '';
  const image2Alt = galleryImage2.alt || 'Gallery Image 3';

  // Always render the three slot-group tiles (with placeholder fallbacks) so they
  // remain visible and editable even before any image has been assigned by hand.
  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-3 gap-3 sm:gap-6 lg:gap-8">
          <EditableSlotGroup
            groupPath="homegallerygrid.gallery_image_0"
            definition={galleryImage0Def}
            className="block"
          >
            {image0Url ? (
              <VibesitesImage
                url={image0Url}
                alt={image0Alt}
                className="w-full rounded-xl object-cover"
                sizes="33vw"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-xl bg-[var(--vibe-color-surface)]"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="homegallerygrid.gallery_image_1"
            definition={galleryImage1Def}
            className="block"
          >
            {image1Url ? (
              <VibesitesImage
                url={image1Url}
                alt={image1Alt}
                className="w-full rounded-xl object-cover md:mt-10"
                sizes="33vw"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-xl bg-[var(--vibe-color-surface)] md:mt-10"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="homegallerygrid.gallery_image_2"
            definition={galleryImage2Def}
            className="block"
          >
            {image2Url ? (
              <VibesitesImage
                url={image2Url}
                alt={image2Alt}
                className="w-full rounded-xl object-cover"
                sizes="33vw"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-xl bg-[var(--vibe-color-surface)]"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </EditableSlotGroup>
        </div>
      </div>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeGalleryGrid',
  displayName: 'Home Gallery Grid',
  tokens: {},
  slotGroups: {
    'homegallerygrid.gallery_image_0': {
      label: 'Gallery Image 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select an appealing, high-quality photo of this property — an interior, amenity, or exterior that shows the community at its best.',
        context:
          'First of three side-by-side tiles in the home page gallery grid; the three tiles together should feel varied, so each tile must use a different photo and ideally a different subject.',
        sourceCollection: 'photo_gallery',
      },
    },
    'homegallerygrid.gallery_image_1': {
      label: 'Gallery Image 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 1,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select an appealing, high-quality photo of this property — an interior, amenity, or exterior that shows the community at its best.',
        context:
          'Second of three side-by-side tiles in the home page gallery grid; each tile must use a different photo from the others and ideally a different subject.',
        sourceCollection: 'photo_gallery',
      },
    },
    'homegallerygrid.gallery_image_2': {
      label: 'Gallery Image 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 2,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'Select an appealing, high-quality photo of this property — an interior, amenity, or exterior that shows the community at its best.',
        context:
          'Third of three side-by-side tiles in the home page gallery grid; each tile must use a different photo from the others and ideally a different subject.',
        sourceCollection: 'photo_gallery',
      },
    },
  },
};

(HomeGalleryGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeGalleryGrid;
